import { useHandler } from "@redotech/react-util/hook";
import { useState } from "react";
import * as z from "zod/v4";
import {
  getLocalStorageWithExpiry,
  removeItemFromLocalStorage,
  setLocalStorageWithExpiry,
} from "../../../utils/local-storage-wrapper";
import { LoggedInPopupState } from "./logged-in-popup";

const DISCOUNT_CODE_STORAGE_KEY = "redo-loyalty-discount-code";
const DISCOUNT_CODE_STORAGE_EXPIRY_HOURS = 1;

const LOGGED_IN_POPUP_STATE_STORAGE_KEY = "redo-loyalty-logged-in-popup-state";
const LOGGED_IN_POPUP_STATE_STORAGE_EXPIRY_HOURS = 1;

const redeemedDiscountStorageParser = z.object({
  discountCode: z.string(),
  pointsUsed: z.number(),
});

export function useAppliedDiscountState(): {
  discountCode: string | undefined;
  pointsUsed: number | undefined;
  onDiscountCodeApplied: ({
    code,
    pointsUsed,
  }: {
    code: string;
    pointsUsed: number;
  }) => Promise<void>;
  onUndoDiscountCode: () => Promise<void>;
  loggedInPopupState: LoggedInPopupState;
  setLoggedInPopupState: (state: LoggedInPopupState) => void;
} {
  const [loggedInPopupState, _setLoggedInPopupState] =
    useState<LoggedInPopupState>(
      getLocalStorageWithExpiry(LOGGED_IN_POPUP_STATE_STORAGE_KEY) ??
        LoggedInPopupState.PRE_REDEMPTION,
    );
  const setLoggedInPopupState = useHandler((state: LoggedInPopupState) => {
    setLocalStorageWithExpiry(
      LOGGED_IN_POPUP_STATE_STORAGE_KEY,
      state,
      LOGGED_IN_POPUP_STATE_STORAGE_EXPIRY_HOURS,
    );
    _setLoggedInPopupState(state);
  });

  const [discountCode, _setDiscountCode] = useState<string | undefined>(() => {
    const stored = getLocalStorageWithExpiry(DISCOUNT_CODE_STORAGE_KEY);
    if (!stored) {
      return undefined;
    }

    return redeemedDiscountStorageParser.parse(stored).discountCode;
  });
  const [pointsUsed, _setPointsUsed] = useState<number | undefined>(() => {
    const stored = getLocalStorageWithExpiry(DISCOUNT_CODE_STORAGE_KEY);
    if (!stored) {
      return undefined;
    }

    return redeemedDiscountStorageParser.parse(stored).pointsUsed;
  });
  const onDiscountCodeApplied = useHandler(
    async ({ code, pointsUsed }: { code: string; pointsUsed: number }) => {
      const parsed = redeemedDiscountStorageParser.parse({
        discountCode: code,
        pointsUsed,
      });

      setLocalStorageWithExpiry(
        DISCOUNT_CODE_STORAGE_KEY,
        parsed,
        DISCOUNT_CODE_STORAGE_EXPIRY_HOURS,
      );
      _setDiscountCode(parsed.discountCode);
      _setPointsUsed(parsed.pointsUsed);

      await navigator.clipboard.writeText(code);
    },
  );

  const onUndoDiscountCode = useHandler(async () => {
    removeItemFromLocalStorage(DISCOUNT_CODE_STORAGE_KEY);
    _setDiscountCode(undefined);
    _setPointsUsed(undefined);
  });

  return {
    discountCode,
    pointsUsed,
    onDiscountCodeApplied,
    onUndoDiscountCode,
    loggedInPopupState,
    setLoggedInPopupState,
  };
}
